import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Chip,
  CircularProgress,
  Pagination,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Challenge,
  ContentType,
  generatedContentApi,
  GeneratedPlan,
  GeneratedScript,
} from 'src/api/generatedContent';
import { useAIAssistantContext } from 'src/hooks/AIAssistant/useAiAssistantContext';
import styles from './AIContentLibrary.module.scss';
import CirclePlanChallenges from './CirclePlanIdeas';
import ContentCard from './ContentCard';
import VideoScript from './VideoScript';

export const AIContentLibrary: FC = () => {
  const {
    contentType,
    setContentType,
    setPrefilledScriptData,
    setSelectedChallengeIndex,
    selectedChallengeIndex,
    resetFormStates,
    switchToTab,
    setScript,
    setCirclePlan,
  } = useAIAssistantContext();
  const [contents, setContents] = useState<GeneratedScript[] | GeneratedPlan[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);
  const [shouldFetchContent, setShouldFetchContent] = useState(true);
  const [activeContent, setActiveContent] = useState<
    GeneratedScript | GeneratedPlan | null
  >(null);

  const handleContentTypeFilter = (type: ContentType) => {
    if (type === ContentType.CIRCLE_PLAN) {
      setContentType(ContentType.CIRCLE_PLAN);
    } else if (type === ContentType.SCRIPT) {
      setContentType(ContentType.SCRIPT);
    }

    setPage(1);
    setActiveContent(null);
    setShouldFetchContent(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await generatedContentApi.getAllGeneratedContent({
        page,
        pageSize: 10,
        searchTerm: searchTerm,
        contentType: contentType,
      });
      setContents(response.result);
      setTotalPages(response.pagination.totalPages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, contentType]);

  useEffect(() => {
    if (shouldFetchContent) {
      fetchContent();
    }
  }, [fetchContent, shouldFetchContent]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleSelectChallenge = (index: number) => {
    setSelectedChallengeIndex(index);
  };

  const handleViewContent = (content: GeneratedScript | GeneratedPlan) => {
    if (!content) {
      toast.error('Invalid content data');
      return;
    }

    try {
      if (contentType === ContentType.CIRCLE_PLAN) {
        setCirclePlan(content as GeneratedPlan);
        setSelectedChallengeIndex(-1);
      } else if (contentType === ContentType.SCRIPT && 'content' in content) {
        setScript(content as GeneratedScript);
      } else {
        toast.error('Invalid content type');
        return;
      }
      setActiveContent(content);
    } catch (error) {
      console.error('Error viewing content:', error);
      toast.error('Failed to view content');
    }
  };

  const handleDevelopChallenge = (challenge: Challenge, planId: string) => {
    resetFormStates();
    setContentType(ContentType.SCRIPT);
    const scriptParams = {
      topic: challenge.title,
      coachingGoal: challenge.challenge,
      keyPoints: [`Response: ${challenge.response}`],
      planId,
      challengeId: challenge._id,
    };
    setPrefilledScriptData(scriptParams);
    switchToTab(0);
  };

  const confirmDelete = (id: string) => {
    setContentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!contentToDelete) {
        return;
      }
      const curContent = (
        contents as Array<GeneratedPlan | GeneratedScript>
      ).find((content) => content._id === contentToDelete);
      if (!curContent) {
        toast.error('Content not found');
        return;
      }

      const isPlan = contentType === ContentType.CIRCLE_PLAN;
      if (isPlan) {
        await generatedContentApi.deleteGeneratedPlan({
          planId: contentToDelete,
        });
      } else {
        await generatedContentApi.deleteGeneratedScript({
          scriptId: contentToDelete,
        });
      }
      toast.success('Content deleted successfully');
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleViewLinkedScripts = async (planId: string) => {
    try {
      setActiveContent(null);
      setLoading(true);
      setShouldFetchContent(false);

      const response = await generatedContentApi.getScriptsByPlanId({
        planId,
        page: 1,
        pageSize: 10,
      });
      const { result, pagination } = response;

      if (!result || result.length === 0) {
        toast.error('Related scripts not found');
        setShouldFetchContent(true);
        setLoading(false);
        return;
      }

      setContentType(ContentType.SCRIPT);
      setPage(1);
      setContents(result);
      setTotalPages(pagination.totalPages ?? 1);
    } catch (error) {
      console.error('Error fetching linked scripts:', error);
      toast.error('Failed to fetch linked scripts');
      setShouldFetchContent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleViewParentPlan = async (planId: string) => {
    try {
      setLoading(true);
      setShouldFetchContent(false);

      const plan = await generatedContentApi.getPlanById(planId);

      if (!plan) {
        toast.error('Parent plan information not found');
        setShouldFetchContent(true);
        setLoading(false);
        return;
      }

      setContentType(ContentType.CIRCLE_PLAN);
      setContents([plan]);
      setPage(1);
      setTotalPages(1);
      setActiveContent(null);

      toast.success('Showing parent circle plan');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching parent plan:', error);
      toast.error('Failed to fetch parent plan');
      setShouldFetchContent(true);
      setLoading(false);
    }
  };

  const handleResetView = async () => {
    try {
      // Clear all active content, filters and states
      setActiveContent(null);
      setCirclePlan(null);
      setScript(null);
      setShouldFetchContent(true);
      setContentType(ContentType.CIRCLE_PLAN);
      setPage(1);
      setSelectedChallengeIndex(-1);

      setLoading(true);

      // ensure state updates
      setTimeout(async () => {
        try {
          const response = await generatedContentApi.getAllGeneratedContent({
            searchTerm: searchTerm,
            contentType: ContentType.CIRCLE_PLAN,
          });

          setContents(response.result);
          setTotalPages(response.pagination.totalPages ?? 1);
        } catch (error) {
          console.error('Error refreshing content:', error);
          toast.error('Failed to refresh content');
        } finally {
          setLoading(false);
        }
      }, 100);
    } catch (error) {
      console.error('Error resetting view:', error);
      toast.error('Failed to reset view');
      setLoading(false);
    }
  };

  const isViewingContent = activeContent !== null;
  const isViewingPlan =
    isViewingContent && contentType === ContentType.CIRCLE_PLAN;

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <TextField
          placeholder="Search content..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />

        <div className={styles.filters}>
          <Chip
            label="Circle Plan"
            onClick={() => handleContentTypeFilter(ContentType.CIRCLE_PLAN)}
            color={
              contentType === ContentType.CIRCLE_PLAN ? 'primary' : 'default'
            }
            className={styles.filterChip}
          />
          <Chip
            label="Video Script"
            onClick={() => handleContentTypeFilter(ContentType.SCRIPT)}
            color={contentType === ContentType.SCRIPT ? 'primary' : 'default'}
            className={styles.filterChip}
          />
        </div>
      </div>

      {!shouldFetchContent && !isViewingContent && (
        <div className={styles.resetViewButton}>
          <Button
            color="primary"
            onClick={() => handleResetView()}
            variant="text"
            sx={{ minWidth: 'unset', padding: '8px' }}
          >
            <CloseIcon />
          </Button>
        </div>
      )}

      {loading && (
        <div className={styles.loading}>
          <CircularProgress />
        </div>
      )}

      {!loading && contents.length === 0 && (
        <div className={styles.noContent}>
          <p>
            No content found. Try creating some new content or adjusting your
            filters.
          </p>
        </div>
      )}

      {!loading && contents.length !== 0 && !isViewingContent && (
        <>
          {contents.map((content) => (
            <ContentCard
              content={content}
              key={content._id}
              onEdit={(content) => {
                handleViewContent(content);
              }}
              onDelete={confirmDelete}
              onView={handleViewContent}
              onViewLinkedScripts={
                contentType === ContentType.CIRCLE_PLAN &&
                (content as GeneratedPlan).challenges &&
                (content as GeneratedPlan).challenges.some(
                  (challenge: Challenge) => challenge.scriptId != null
                )
                  ? () => handleViewLinkedScripts(content._id)
                  : undefined
              }
              onViewParentPlan={
                contentType !== ContentType.CIRCLE_PLAN &&
                (content as GeneratedScript).planId
                  ? () =>
                      handleViewParentPlan((content as GeneratedScript).planId!)
                  : undefined
              }
            />
          ))}
          <div className={styles.pagination}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </div>
        </>
      )}

      {!loading && isViewingContent && (
        <div className={styles.contentDetailView}>
          <div className={styles.closeButton}>
            <Button
              color="primary"
              onClick={() => handleResetView()}
              variant="text"
              sx={{ minWidth: 'unset', padding: '8px' }}
            >
              <CloseIcon />
            </Button>
          </div>

          {isViewingPlan && activeContent && (
            <CirclePlanChallenges
              circlePlan={activeContent as GeneratedPlan}
              selectedChallengeIndex={selectedChallengeIndex}
              onSelectChallenge={handleSelectChallenge}
              onDevelopChallenge={handleDevelopChallenge}
            />
          )}

          {!isViewingPlan && activeContent && (
            <VideoScript script={activeContent as GeneratedScript} />
          )}
        </div>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this content? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
